import * as HoverCard from '@radix-ui/react-hover-card'
import { useState, type ReactNode } from 'react'
import { FileText } from 'lucide-react'
import { sourceNumber } from '@/lib/citations'
import { formatFull, formatRelative } from '@/lib/time'
import { useCitations } from './citationContext'

/**
 * Inline citation pill. Hover (HoverCard) or keyboard focus (our own handler)
 * shows a preview; click opens the sources panel at this source. Markers whose
 * id isn't in the message's sources fall back to plain text.
 */
export function CitationChip({ id, fallback }: { id: string; fallback: ReactNode }) {
  const citations = useCitations()
  const [open, setOpen] = useState(false)
  const source = citations?.sourcesById.get(id)
  if (!citations || !source) return <>{fallback}</>

  const n = sourceNumber(id)
  const section = source.section
    .split('>')
    .map((s) => s.trim())
    .filter(Boolean)
    .join(' › ')

  return (
    <HoverCard.Root open={open} onOpenChange={setOpen} openDelay={150} closeDelay={100}>
      <HoverCard.Trigger asChild>
        <button
          type="button"
          className="mx-[2px] inline-flex h-[19px] min-w-[19px] -translate-y-[1.5px] items-center justify-center rounded-full bg-accent/10 px-1.5 align-middle text-[10.5px] font-semibold leading-none text-accent-text transition-all duration-200 ease-out hover:scale-110 hover:bg-accent hover:text-accent-ink focus-visible:bg-accent focus-visible:text-accent-ink active:scale-95 dark:bg-accent/20"
          aria-label={`Source ${n}: ${source.title}`}
          onFocus={() => setOpen(true)}
          onBlur={() => setOpen(false)}
          onClick={() => {
            setOpen(false)
            citations.onOpen(id)
          }}
        >
          {n}
        </button>
      </HoverCard.Trigger>
      <HoverCard.Portal>
        <HoverCard.Content
          side="top"
          align="center"
          sideOffset={6}
          collisionPadding={12}
          className="glass z-50 w-80 origin-[var(--radix-hover-card-content-transform-origin)] rounded-2xl p-4 text-left shadow-popover data-[state=open]:motion-safe:animate-pop-in data-[state=closed]:motion-safe:animate-pop-out"
        >
          <div className="flex items-start gap-2.5">
            <span className="flex size-7 shrink-0 items-center justify-center rounded-lg bg-accent/10 text-accent-text">
              <FileText className="size-3.5" aria-hidden />
            </span>
            <div className="min-w-0">
              <p className="text-sm font-semibold leading-5 text-ink">{source.title}</p>
              {section && <p className="mt-0.5 truncate text-xs text-ink-muted">{section}</p>}
              <p className="mt-1 text-xs text-ink-faint">
                {source.source_name}
                {source.updated_at && (
                  <>
                    {' · '}
                    <time dateTime={source.updated_at} title={formatFull(source.updated_at)}>
                      updated {formatRelative(source.updated_at)}
                    </time>
                  </>
                )}
              </p>
            </div>
          </div>
          {source.snippet && (
            <p className="mt-3 line-clamp-5 border-t border-line/70 pt-3 text-xs leading-5 text-ink-muted">
              {source.snippet}
            </p>
          )}
          <p className="mt-2 text-[11px] font-medium text-accent-text">Click to view in sources</p>
        </HoverCard.Content>
      </HoverCard.Portal>
    </HoverCard.Root>
  )
}
